import type { VacationDpFilterKey } from '../../models/vacationDpBoard.types';
import type { CellTone } from './vacationDpTable.types';

export type DpColumnProfile = 'request' | 'balance' | 'periods' | 'general';

export type DpColumnId = 'collaborator' | 'days' | 'deadline' | 'period1' | 'period2' | 'stage' | 'sell' | 'conflict' | 'actions';

export const DP_COLUMN_IDS: DpColumnId[] = [
    'actions',
    'collaborator',
    'days',
    'deadline',
    'period1',
    'period2',
    'stage',
    'sell',
    'conflict',
];

export const LOCKED_COLUMNS: DpColumnId[] = ['actions', 'collaborator'];

export function resolveColumnProfile(activeFilter: VacationDpFilterKey | null): DpColumnProfile {
    if (!activeFilter) return 'general';
    const requestFilters: VacationDpFilterKey[] = ['awaitingApproval', 'sellPending', 'conflict'];
    if (requestFilters.includes(activeFilter)) return 'request';
    const periodFilters: VacationDpFilterKey[] = ['onVacation', 'scheduled'];
    if (periodFilters.includes(activeFilter)) return 'periods';
    return 'balance';
}

export const COLUMN_LABELS = {
    collaborator: 'Colaborador',
    deadline: 'Prazo',
    stage: 'Etapa',
    sell: 'Abono',
    conflict: 'Conflito',
    actions: 'Ações',
} as const;

export const COLUMN_LABELS_BY_PROFILE: Record<DpColumnProfile, Record<string, string>> = {
    request: {
        days: 'Solicitado',
        deadline: 'Prazo',
    },
    balance: {
        days: 'Saldo',
        deadline: 'Vencimento',
    },
    periods: {
        days: 'Saldo',
        period1: '1º período',
        period2: '2º período',
    },
    general: {
        days: 'Saldo',
        deadline: 'Prazo',
    },
};

export const COLUMN_VISIBILITY_BY_PROFILE: Record<DpColumnProfile, Record<string, boolean>> = {
    request: {
        collaborator: true,
        days: true,
        deadline: true,
        period1: false,
        period2: false,
        stage: true,
        sell: true,
        conflict: true,
        actions: true,
    },
    balance: {
        collaborator: true,
        days: true,
        deadline: true,
        period1: false,
        period2: false,
        stage: true,
        sell: false,
        conflict: false,
        actions: true,
    },
    periods: {
        collaborator: true,
        days: true,
        deadline: false,
        period1: true,
        period2: true,
        stage: true,
        sell: false,
        conflict: false,
        actions: true,
    },
    general: {
        collaborator: true,
        days: true,
        deadline: true,
        period1: false,
        period2: false,
        stage: true,
        sell: true,
        conflict: true,
        actions: true,
    },
};

export const CELL_TONE_CLASSES: Record<CellTone, string> = {
    none: '',
    danger: 'bg-red-50 text-red-700 font-semibold',
    warning: 'bg-amber-50 text-amber-700',
    primary: 'bg-primary-50 text-primary-700',
    success: 'bg-emerald-50 text-emerald-700',
    muted: 'text-raro-gray-400',
};

export const DEADLINE_WARNING_DAYS = 30;

export const UNALLOCATED_WARNING_LABEL = 'Histórico maior que direito — conferir cadastro';

export const NO_HISTORY_LABEL = 'Sem histórico lançado';
export const NO_HISTORY_HINT = 'Nenhuma férias do histórico foi lançada neste cadastro. O saldo pode estar superestimado.';

export function getLegacyTakenLabel(days: number): string {
    return `${days}d do histórico`;
}

export function getScheduledLabel(days: number): string {
    return `${days}d agendados`;
}

export function getPendingLabel(days: number): string {
    return `${days}d aguardando aprovação`;
}

export const NOT_APPLICABLE = '–';

export const NO_CONFLICT = 'Não';

export const NO_REQUEST_LABEL = 'Não solicitado';
export const IN_ACQUISITION_LABEL = 'Em aquisição';

export const PROJECTED_DEADLINE_NO_REQUEST = 'Sem solicitação em aberto';
export const PROJECTED_DEADLINE_NEXT = 'Próximo prazo';

export const OPEN_REQUEST_LABEL = 'Ver solicitação';
export const NO_REQUEST_ACTION_TITLE = 'Este colaborador não tem solicitação aberta';

export const SORT_HINT =
    'Ordenado por urgência por padrão: vencidos primeiro (do mais antigo), depois por proximidade do vencimento.';

export const EMPTY_DEFAULT = 'Nenhum colaborador com saldo de férias calculado.';
export const EMPTY_FILTERED = 'Nenhum colaborador nesta situação.';
export const EMPTY_SEARCH = 'Nenhum colaborador encontrado para esta busca.';

export const LOADING_MESSAGE = 'Carregando colaboradores...';

export const VIRTUALIZE_THRESHOLD = 25;

export const ROW_ESTIMATE_PX = 56;

export const ROW_OVERSCAN = 8;

export const SCROLL_MAX_HEIGHT = 'max-h-[70vh]';

export const SCROLL_REGION_LABEL = 'Lista de colaboradores';

export const CARD_FIELD_LABELS = {
    approvalDeadline: 'Prazo de aprovação',
    sell: 'Abono',
    conflict: 'Conflito',
} as const;

function pluralDays(days: number): string {
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
}

export function formatDeadlineLabel(daysUntil: number | null): string {
    if (daysUntil === null) return IN_ACQUISITION_LABEL;
    if (daysUntil < 0) return `vencido há ${pluralDays(Math.abs(daysUntil))}`;
    if (daysUntil === 0) return 'vence hoje';
    return `vence em ${pluralDays(daysUntil)}`;
}

export function buildDeadlineText(
    daysUntil: number | null,
    expiredDays: number,
    hasRequest = true
): { primary: string; secondary: string | null } {
    // Sem solicitação aberta e prazo projetado: não mostrar contagem
    if (!hasRequest && daysUntil !== null && daysUntil > 30) {
        const date = new Date();
        date.setDate(date.getDate() + (daysUntil || 0));
        const dateStr = date.toLocaleDateString('pt-BR');
        return {
            primary: PROJECTED_DEADLINE_NO_REQUEST,
            secondary: `${PROJECTED_DEADLINE_NEXT}: ${dateStr}`,
        };
    }

    if (expiredDays > 0) {
        return {
            primary: `${pluralDays(expiredDays)} vencidos`,
            secondary: daysUntil !== null ? formatDeadlineLabel(daysUntil) : null,
        };
    }

    return { primary: formatDeadlineLabel(daysUntil), secondary: null };
}

export function resolveDeadlineTone(
    daysUntil: number | null,
    isExpired: boolean,
    isProjected = false
): CellTone {
    if (isExpired) return 'danger';
    if (isProjected) return 'muted';
    if (daysUntil === null) return 'muted';
    if (daysUntil <= DEADLINE_WARNING_DAYS) return 'warning';
    return 'none';
}

export const SELL_SHORT_LABELS: Record<string, string> = {
    None: NOT_APPLICABLE,
    Pending: 'Interesse manifestado',
    Approved: 'Aprovado',
    Rejected: 'Reprovado',
};

export const DEFAULT_COLUMN_LABELS: Record<DpColumnId, string> = {
    actions: 'Ações',
    collaborator: 'Colaborador',
    days: 'Saldo',
    deadline: 'Prazo',
    period1: '1º período',
    period2: '2º período',
    stage: 'Etapa',
    sell: 'Abono',
    conflict: 'Conflito',
};

export function getColumnLabel(profile: DpColumnProfile, id: DpColumnId): string {
    return COLUMN_LABELS_BY_PROFILE[profile][id] ?? DEFAULT_COLUMN_LABELS[id];
}

export function getColumnVisibilityStorageKey(email: string): string {
    return `radar:vacation-dp-columns:v1:${email}`;
}
